// v1.0.0 - Production Release
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/index.css';
import './styles/components.css';
import './styles/responsive.css';
import './styles/App.css';

// Lazy load pages for code splitting
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const TimesheetEntryPage = React.lazy(() => import('./pages/TimesheetEntryPage'));
const ViewTimesheetsPage = React.lazy(() => import('./pages/ViewTimesheetsPage'));
const ManagerTimeReviewPage = React.lazy(() => import('./pages/ManagerTimeReviewPage'));
const PartnerDashboardPage = React.lazy(() => import('./pages/PartnerDashboardPage'));
const BillingControlPage = React.lazy(() => import('./pages/BillingControlPage'));
const AdminBillingInstructionsPage = React.lazy(() => import('./pages/AdminBillingInstructionsPage'));
const AdminReceivablesPage = React.lazy(() => import('./pages/AdminReceivablesPage'));
const EngagementSetupPage = React.lazy(() => import('./pages/EngagementSetupPage'));
const PeriodCloseReportPage = React.lazy(() => import('./pages/PeriodCloseReportPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const NonChargeableTypesPage = React.lazy(() => import('./pages/NonChargeableTypesPage'));
const NonChargeableApprovalPage = React.lazy(() => import('./pages/NonChargeableApprovalPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const AdminTimesheetStatusPage = React.lazy(() => import('./pages/AdminTimesheetStatusPage'));
const RecoveryPage = React.lazy(() => import('./pages/RecoveryPage'));
const GradeMultipliersPage = React.lazy(() => import('./pages/GradeMultipliersPage'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="loading-container">
    <h2>Loading...</h2>
  </div>
);

// Main App Component (with routes)
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={<Navigate to="/login" replace />}
        />
        <Route
          path="/signup"
          element={<Navigate to="/login" replace />}
        />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <ResetPasswordPage />}
        />

        {/* Public Pages */}
        <Route
          path="/terms"
          element={<TermsPage />}
        />
        <Route
          path="/privacy"
          element={<PrivacyPage />}
        />
        <Route
          path="/about"
          element={<AboutPage />}
        />
        <Route
          path="/help"
          element={<HelpPage />}
        />
        <Route
          path="/contact"
          element={<ContactPage />}
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/log-time"
          element={
            <ProtectedRoute requiredRole={['Staff', 'Supervisor', 'Manager', 'Partner', 'Admin']}>
              <TimesheetEntryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/timesheets"
          element={
            <ProtectedRoute requiredRole={['Staff', 'Supervisor', 'Manager', 'Partner', 'Admin']}>
              <ViewTimesheetsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/team-review"
          element={
            <ProtectedRoute requiredRole={['Supervisor', 'Manager', 'Partner', 'Admin']}>
              <ManagerTimeReviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/partner-dashboard"
          element={
            <ProtectedRoute requiredRole={['Partner']}>
              <PartnerDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/billing-control"
          element={
            <ProtectedRoute requiredRole={['Manager', 'Partner']}>
              <BillingControlPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/billing-instructions"
          element={
            <ProtectedRoute requiredRole={['Admin']}>
              <AdminBillingInstructionsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/receivables"
          element={
            <ProtectedRoute requiredRole={['Admin']}>
              <AdminReceivablesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/engagement-setup"
          element={
            <ProtectedRoute requiredRole={['Supervisor', 'Manager', 'Partner', 'Admin']}>
              <EngagementSetupPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/period-close"
          element={
            <ProtectedRoute requiredRole={['Manager', 'Partner', 'Admin']}>
              <PeriodCloseReportPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/recovery"
          element={
            <ProtectedRoute requiredRole={['Supervisor', 'Manager', 'Partner']}>
              <RecoveryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/grade-multipliers"
          element={
            <ProtectedRoute requiredRole={['Partner']}>
              <GradeMultipliersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute requiredRole={['Partner', 'Admin']}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRole={['Admin', 'Partner', 'SuperAdmin']}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/non-chargeable-types"
          element={
            <ProtectedRoute requiredRole={['Partner']}>
              <NonChargeableTypesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/non-chargeable-approval"
          element={
            <ProtectedRoute requiredRole={['Partner']}>
              <NonChargeableApprovalPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute requiredRole={['Staff', 'Supervisor', 'Manager', 'Partner', 'Admin']}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRole={['Partner', 'Manager', 'Supervisor']}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/timesheet-status"
          element={
            <ProtectedRoute requiredRole={['Admin', 'Partner']}>
              <AdminTimesheetStatusPage />
            </ProtectedRoute>
          }
        />

        {/* URL aliases — redirect common alternate paths to canonical routes */}
        <Route path="/billing" element={<Navigate to="/billing-control" replace />} />
        <Route path="/engagements" element={<Navigate to="/engagement-setup" replace />} />
        <Route path="/user-management" element={<Navigate to="/admin/users" replace />} />
        <Route path="/non-chargeable-approvals" element={<Navigate to="/non-chargeable-approval" replace />} />

        {/* Redirect root to login or dashboard based on auth status */}
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />}
        />

        {/* 404 Page */}
        <Route
          path="*"
          element={<NotFoundPage />}
        />
      </Routes>
    </Suspense>
  );
};

// Main App with Auth Provider and Error Boundary
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
